import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import NewRegistration from './NewRegistration';
import ReInscriptionManager from '../components/reinscriptions/ReInscriptionManager';
import { UserPlus, RefreshCw, AlertCircle, BookOpenText } from "lucide-react";
import ProtectedComponent from '../components/ProtectedComponent';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import GuideUtilisationDrawer from '../components/GuideUtilisationDrawer';

const RegistrationManager = () => {
  const [selectedMainTab, setSelectedMainTab] = useState('inscription');
  const [activeYear, setActiveYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const yearResponse = await api.anneesScolaires.getActive();
        if (yearResponse.success && yearResponse.data) {
          setActiveYear(yearResponse.data);
        } else {
          setError("Aucune année scolaire active n'est définie");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#246990]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        <AlertCircle className="inline-block mr-2" size={20} />
        {error}
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-[#246990]">Admissions</h1>
        <button
          type="button"
          onClick={() => setIsGuideOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#246990]/20 bg-[#246990]/10 px-3 py-2 text-sm font-semibold text-[#246990] transition hover:bg-[#246990]/15"
        >
          <BookOpenText size={16} />
          Guide d'utilisation
        </button>
      </div>
      {/* Navigation principale avec Tabs */}
      <Tabs
        defaultValue="inscription"
        value={selectedMainTab}
        onValueChange={setSelectedMainTab}
        className="w-full"
      >
        <TabsList className="grid grid-cols-2 mb-8 bg-[#246990]/10 w-full">
          <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.INSCRIPTIONS" action="CREATE">
            <TabsTrigger
              value="inscription"
              className="flex items-center justify-center gap-2 data-[state=active]:bg-[#246990] data-[state=active]:text-white min-w-0"
            >
              <UserPlus size={16} />
              <span className="hidden md:inline whitespace-nowrap">Inscription</span>
            </TabsTrigger>
          </ProtectedComponent>

          <ProtectedComponent module="ELEVES" sousModule="ADMISSIONS.REINSCRIPTIONS" action="CREATE">
            <TabsTrigger
              value="reinscription"
              className="flex items-center justify-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white min-w-0"
            >
              <RefreshCw size={16} />
              <span className="hidden md:inline whitespace-nowrap">Réinscription</span>
            </TabsTrigger>
          </ProtectedComponent>
        </TabsList>

        {/* Contenu des onglets */}
        <TabsContent value="inscription" className="w-full">
          <ProtectedComponent
            module="ELEVES"
            sousModule="ADMISSIONS.INSCRIPTIONS"
            action="CREATE"
            fallback={<div className="p-8 text-center">Vous n'avez pas l'autorisation de créer des inscriptions.</div>}
          >
            <NewRegistration activeYear={activeYear} />
          </ProtectedComponent>
        </TabsContent>

        <TabsContent value="reinscription" className="w-full">
          <ProtectedComponent
            module="ELEVES"
            sousModule="ADMISSIONS.REINSCRIPTIONS"
            action="CREATE"
            fallback={<div className="p-8 text-center">Vous n'avez pas l'autorisation de gérer les réinscriptions.</div>}
          >
            <ReInscriptionManager activeYear={activeYear} />
          </ProtectedComponent>
        </TabsContent>
      </Tabs>
      <GuideUtilisationDrawer
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        module="admission"
      />
    </div>
  );
};

export default RegistrationManager;